import { useEffect, useState } from 'react'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'


interface PercentageInputProps {
  inputId: string
  value: number | null
  placeholder?: string
  invalid?: boolean
  onChange: (value: number | null) => void
  onBlur?: () => void
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

const PARTIAL_PERCENTAGE = /^\d{0,3}(,\d{0,2})?$/
const MAX_PERCENTAGE = 100

function parsePercentage(text: string): number | null {
  const normalized = text.replace(',', '.')
  if (normalized === '' || normalized === '.') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function formatPercentageText(value: number | null): string {
  return value == null ? '' : String(value).replace('.', ',')
}

export default function PercentageInput({
  inputId,
  value,
  placeholder,
  invalid,
  onChange,
  onBlur,
  ...ariaProps
}: Readonly<PercentageInputProps>) {
  const [text, setText] = useState(() => formatPercentageText(value))

  useEffect(() => {
    if (parsePercentage(text) !== value) setText(formatPercentageText(value))
  }, [value])

  function handleChange(raw: string) {
    const next = raw.replace('.', ',')
    if (!PARTIAL_PERCENTAGE.test(next)) return
    const parsed = parsePercentage(next)
    if (parsed != null && parsed > MAX_PERCENTAGE) return
    setText(next)
    onChange(parsed)
  }

  function handleBlur() {
    if (text.endsWith(',')) setText(text.slice(0, -1))
    onBlur?.()
  }

  return (
    <span className='chd-percentage-input'>
      <InputText
        id={inputId}
        value={text}
        inputMode='decimal'
        autoComplete='off'
        placeholder={placeholder}
        className={classNames({ 'p-invalid': invalid })}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={handleBlur}
        {...ariaProps}
      />
      <span className='chd-percentage-suffix' aria-hidden='true'>%</span>
    </span>
  )
}
